import { ReactNode } from 'react';
import { useIsClientReady, formatMoney } from '@belong/common';
import { IconLock, IconInfo } from '@belong/icons';
import { ButtonBase, Spinner, Text, Tooltip } from '@belong/ui';
import { CoverageRegionModel, PayoutPlanVariationModel } from 'src/api/models';
import { usePayoutPlans, useRegions } from 'src/features/app';
import { useModal } from 'src/hooks/useModal';
import { From, RegionSelect } from 'src/modals/region-select/region-select';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import { HOMEOWNERS_STRINGS } from '../../homeowners.strings';

type TablePayoutPlansProps = {
  keyName:
    | 'managementFeePercentage'
    | 'placementFeePercentage'
    | 'renewalFeePercent';
  regionId?: CoverageRegionModel['id'];
  showTooltip?: boolean;
  tooltipText?: string | ReactNode;
  from?: From;
  payoutPlan?: Record<string, PayoutPlanVariationModel>;
};

export function TablePayoutPlans({
  keyName,
  regionId,
  showTooltip = false,
  tooltipText,
  from = 'homeowners',
  payoutPlan,
}: TablePayoutPlansProps): JSX.Element {
  const isClientReady = useIsClientReady();
  const [
    isRegionSelectModalOpen,
    openRegionSelectModal,
    closeRegionSelectModal,
  ] = useModal();
  const { regions } = useRegions();
  const selectedRegion = useStore(regionSelector);
  const { payoutPlans } = usePayoutPlans();
  const propRegion = regions.find((region) => region.id === regionId);
  const { status, id } = propRegion || selectedRegion;

  if (!isClientReady || (!payoutPlan && !payoutPlans)) {
    return (
      <div className="relative size-full">
        <Spinner showBg={false} />
      </div>
    );
  }

  if (!status || !id) {
    return (
      <>
        <ButtonBase onClick={openRegionSelectModal}>
          <div className="flex items-center">
            <div className="mb-2xs mr-2xs sm:mr-xs">
              <IconLock width={20} className="text-green" />
            </div>
            {renderText(HOMEOWNERS_STRINGS['priceTable.unlockButton'])}
          </div>
        </ButtonBase>
        <RegionSelect
          from={from}
          isOpen={isRegionSelectModalOpen}
          onDismiss={closeRegionSelectModal}
        />
      </>
    );
  }

  const TBD = HOMEOWNERS_STRINGS['priceTable.toBeDetermined'];

  const getPayoutPlanValue = () => {
    if (
      status !== 'Other' &&
      (payoutPlan?.Monthly?.defaultPayoutPlanType === 'Variable' ||
        payoutPlan?.Monthly?.defaultPayoutPlanType === 'VariableNoMinimum')
    ) {
      return `${payoutPlan?.Monthly[keyName]}%`;
    }

    if (
      status !== 'Other' &&
      payoutPlan?.Monthly?.defaultPayoutPlanType === 'Fixed'
    ) {
      if (keyName === 'renewalFeePercent') {
        return formatMoney(payoutPlan?.Monthly?.renewalFeeFixedAmount);
      }

      return `${
        keyName === 'managementFeePercentage'
          ? `${formatMoney(payoutPlan?.Monthly?.managementFeeFixedAmount)}/Month`
          : formatMoney(payoutPlan?.Monthly?.placementFeeFixedAmount)
      }`;
    }

    return TBD;
  };

  const payoutPlanValue = getPayoutPlanValue();
  const hasTooltip = showTooltip && tooltipText;

  return (
    <div className="flex flex-row justify-center">
      {payoutPlanValue != TBD && hasTooltip ? (
        <div className="ml-2xs flex items-center">
          <Tooltip
            content={
              <div className="bg-dark-navy p-2sm max-w-md rounded ">
                <Text fontWeight="semibold" className="text-white">
                  {tooltipText}
                </Text>
              </div>
            }
            arrowProps={{
              className: 'fill-dark-navy',
            }}
          >
            <div className="gap-2xs flex cursor-pointer flex-row justify-center">
              {renderText(payoutPlanValue)}
              <IconInfo width={20} />
            </div>
          </Tooltip>
        </div>
      ) : (
        renderText(payoutPlanValue)
      )}
    </div>
  );
}

// Optional: `TBD` comes from a strings lookup, so the value this renders can
// be absent, and `Text` renders nothing for it.
const renderText = (text?: string) => (
  <>
    <Text fontWeight="semibold" variant="p1" className="md:hidden">
      {text}
    </Text>
    <Text fontWeight="semibold" variant="body" className="hidden md:block">
      {text}
    </Text>
  </>
);
